'use client';

/**
 * SubPageTabs — Phase 6.5 IA primitive.
 *
 * Horizontal tab bar that sits at the top of every section page that
 * has sub-pages. The Nav v2 sidebar is intentionally limited to 2
 * levels (domain → item); deeper navigation is rendered IN-PAGE via
 * this primitive (Linear / Notion / macOS pattern, ratified with Paul
 * 2026-05-27).
 *
 * Example:
 *   /hr               → renders SubPageTabs with [Overview, Onboarding,
 *                       Performance, Training] tabs; Overview is active
 *   /hr/onboarding    → same tabs; Onboarding is active
 *
 * Anatomy:
 *   ┌─────────────────────────────────────────────────────┐
 *   │ Overview  Onboarding  Performance  Training       › │
 *   └─────────────────────────────────────────────────────┘
 *
 * - One row of horizontally-scrollable tabs (overflow-x: auto, no
 *   horizontal scrollbar on hover-capable devices). When the row
 *   overflows, soft fade edges + chevron scroll buttons appear on the
 *   clipped side(s) so off-screen tabs stay discoverable (roster UX
 *   audit 2026-06-10 P0-3 — previously tabs clipped with no affordance).
 * - Active tab = current pathname matches the tab's `href` OR one of
 *   its `activeFor` prefixes (longest-prefix winner across all tabs'
 *   candidates — same pattern as Nav v2 sidebar). `activeFor` lets a
 *   grouped tab claim deep links that live under a different path
 *   (e.g. roster "Approvals" → /roster/timesheets also owns
 *   /roster/recalls).
 * - Each tab is a Link → uses framework router (Next.js Link via the
 *   `linkRender` prop so this stays framework-agnostic).
 * - Optional `counts` map renders a small badge next to the tab label
 *   (e.g. "Pending (3)" / "Open (7)").
 * - Keyboard: Tab through; arrow keys NOT bound (links use Tab order).
 */

import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Resolve which tab should be marked active for a given current
 * pathname. Longest-prefix-winner across each tab's `href` plus its
 * optional `activeFor` prefixes (matches the Nav v2 sidebar's
 * active-state rule). When two tabs both claim a path, the tab whose
 * matching candidate is LONGEST wins — e.g. `/roster/proposals/conflicts`
 * in a "Data health" tab's activeFor beats `/roster/proposals` in
 * "Plan"'s. Exported as a pure function for unit testing.
 */
export function resolveActiveTab(
  tabs: ReadonlyArray<SubPageTab>,
  currentPath: string,
): string | null {
  let best: { href: string; length: number } | null = null;
  for (const tab of tabs) {
    const candidates: readonly string[] = tab.activeFor ? [tab.href, ...tab.activeFor] : [tab.href];
    for (const candidate of candidates) {
      if (currentPath === candidate || currentPath.startsWith(`${candidate}/`)) {
        if (!best || candidate.length > best.length) {
          best = { href: tab.href, length: candidate.length };
        }
      }
    }
  }
  return best?.href ?? null;
}

export interface SubPageTab {
  /** Stable id used as the React key. */
  id: string;
  /** Display label for the tab. */
  label: string;
  /** Pathname this tab represents (for active-state + navigation). */
  href: string;
  /**
   * Optional count badge rendered next to the label
   * (e.g. "Open (7)"). Hidden when undefined / null / 0.
   */
  count?: number | null;
  /**
   * Additional path prefixes that should highlight this tab (for
   * grouped IAs where deep links live outside the tab's own href
   * subtree). Active-state only — never rendered, never navigated to.
   */
  activeFor?: readonly string[];
}

export interface SubPageTabsProps {
  /** Tab definitions in display order. */
  tabs: ReadonlyArray<SubPageTab>;
  /** Current pathname for active-state (longest-prefix-winner). */
  currentPath: string;
  /**
   * Render-prop for the navigation link. Defaults to a plain anchor.
   * Web app should wire to a Next.js `<Link>` to keep prefetch +
   * soft-navigation behaviour.
   */
  linkRender?: (props: {
    href: string;
    className: string;
    'aria-current': 'page' | undefined;
    children: React.ReactNode;
  }) => React.ReactNode;
  className?: string;
}

export function SubPageTabs({ tabs, currentPath, linkRender, className }: SubPageTabsProps) {
  const scrollerRef = React.useRef<HTMLDivElement | null>(null);
  const [overflow, setOverflow] = React.useState<{ left: boolean; right: boolean }>({
    left: false,
    right: false,
  });

  const updateOverflow = React.useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    const left = el.scrollLeft > 1;
    const right = el.scrollLeft < maxScrollLeft - 1;
    setOverflow((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);

  React.useEffect(() => {
    updateOverflow();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateOverflow, { passive: true });
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateOverflow) : null;
    resizeObserver?.observe(el);
    window.addEventListener('resize', updateOverflow);
    return () => {
      el.removeEventListener('scroll', updateOverflow);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateOverflow);
    };
  }, [updateOverflow, tabs]);

  const scrollByStep = (direction: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * Math.max(160, el.clientWidth * 0.6), behavior: 'smooth' });
  };

  if (tabs.length === 0) return null;
  const activeHref = resolveActiveTab(tabs, currentPath);

  // Codex Phase 6.5 pass 2 P3: tabs no longer use `sticky top-0` — the
  // dashboard shell's header is already `sticky top-0 z-30 h-14`, and
  // a tab bar with lower z-index pinned underneath. Static positioning
  // is correct here; the dashboard header already gives users a
  // constant "where am I" anchor at the page top.
  return (
    <nav
      aria-label="Section navigation"
      className={cn('relative -mt-px w-full border-b border-slate-200 bg-white', className)}
      data-sub-page-tabs
    >
      <div
        ref={scrollerRef}
        className={cn(
          'flex w-full items-center gap-0 overflow-x-auto px-4',
          '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        )}
      >
        {tabs.map((tab) => {
          const isActive = activeHref === tab.href;
          const linkClass = cn(
            'relative inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2.5 text-[12.5px] font-medium transition',
            'focus:outline-none focus-visible:bg-slate-50',
            isActive
              ? 'border-statura-green-accessible text-statura-green-accessible'
              : 'border-transparent text-slate-500 hover:text-statura-ink',
          );
          const children = (
            <>
              {tab.label}
              {typeof tab.count === 'number' && tab.count > 0 && (
                <span
                  className={cn(
                    'inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-semibold',
                    isActive
                      ? 'bg-statura-green-accessible/15 text-statura-green-accessible'
                      : 'bg-slate-100 text-slate-600',
                  )}
                  aria-label={`${tab.count} items`}
                >
                  {tab.count > 99 ? '99+' : tab.count}
                </span>
              )}
            </>
          );
          const ariaCurrent = isActive ? ('page' as const) : undefined;
          return (
            <React.Fragment key={tab.id}>
              {linkRender ? (
                linkRender({
                  href: tab.href,
                  className: linkClass,
                  'aria-current': ariaCurrent,
                  children,
                })
              ) : (
                <a href={tab.href} className={linkClass} aria-current={ariaCurrent}>
                  {children}
                </a>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Overflow affordances — fade edge + chevron on each clipped side. */}
      {overflow.left && (
        <>
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-white to-transparent"
          />
          <button
            type="button"
            aria-label="Scroll tabs left"
            onClick={() => scrollByStep(-1)}
            className="absolute left-1 top-1/2 z-10 -translate-y-1/2 rounded-full border border-slate-200 bg-white p-0.5 text-slate-500 shadow-sm hover:text-statura-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </button>
        </>
      )}
      {overflow.right && (
        <>
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-white to-transparent"
          />
          <button
            type="button"
            aria-label="Scroll tabs right"
            onClick={() => scrollByStep(1)}
            className="absolute right-1 top-1/2 z-10 -translate-y-1/2 rounded-full border border-slate-200 bg-white p-0.5 text-slate-500 shadow-sm hover:text-statura-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-statura-green-accessible"
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </button>
        </>
      )}
    </nav>
  );
}
